import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";

import FilterListItem from "@client/components/modules/FilterList/Item";
import FilterListWrapper from "@client/components/modules/FilterList/Wrapper";
import ListTemplate from "@client/components/templates/ListTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import ExportPdfButton from "@client/utils/ExportPdfButton";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import customerImage from "@client/components/ui/sharedImages/customer.svg";
import customerStore from "@client/stores/CustomerStore";
import licenceStore from "@client/stores/LicenceStore";
import Style from "@client/utils/Style";
import { Customer } from "@typings/Customer";

import CustomerModal from "../CustomerModal";

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

const ExportButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
`;

type State = {
  showCustomerModal: boolean;
  selectedCustomers: Customer[];
  showDeleteAlert: boolean;
  showDeleteErrorAlert: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
};

@observer
class CustomersPage extends React.Component<Props, State> {
  state: State = {
    showCustomerModal: false,
    selectedCustomers: [],
    showDeleteAlert: false,
    showDeleteErrorAlert: false,
  };

  componentDidMount() {
    customerStore.load();
    licenceStore.load();
  }

  handleItemClick(customer: Customer) {
    this.props.onNavigate(`/customers/${customer.id}`);
  }

  handleReloadButtonClick() {
    licenceStore.load();
    customerStore.load({ forceShowLoading: true });
  }

  itemFilterFunction(
    customer: Customer,
    _?: string | null,
    filterText?: string,
  ) {
    filterText = (filterText || "").toLowerCase();
    if (
      customer.id.toString().indexOf(filterText) === -1 &&
      customer.name.toLowerCase().indexOf(filterText) === -1 &&
      (!customer.description ||
        customer.description.toLowerCase().indexOf(filterText) === -1) &&
      (!customer.email ||
        customer.email.toLowerCase().indexOf(filterText) === -1) &&
      customer.created_by.toLowerCase().indexOf(filterText) === -1
    ) {
      return false;
    }

    return true;
  }

  getListColumns(customer: Customer) {
    const licences = licenceStore.licences.filter(
      l => l.customer_id === customer.id,
    );
    const total = licences.reduce(
      (t, l) => ({ r: t.r + l.replicas, m: t.m + l.migrations }),
      { r: 0, m: 0 },
    );

    return [
      {
        name: "Email",
        value: customer.email || "-",
        style: { minWidth: "204px", maxWidth: "204px" },
      },
      {
        name: "Licences",
        value: `${licences.length}`,
        style: { minWidth: "94px", maxWidth: "94px" },
      },
      {
        name: "Total Migrations / Replicas",
        value: `${total.m} / ${total.r}`,
        style: { minWidth: "176px", maxWidth: "176px" },
      },
      {
        name: "Created at",
        value: moment(customer.created_at).format("DD MMM YYYY HH:mm"),
        style: { minWidth: "144px", maxWidth: "144px" },
      },
      {
        name: "Created by",
        value: `${customer.created_by}`,
        style: { minWidth: "84px", maxWidth: "84px" },
      },
    ];
  }

  handleDeleteBulkAction() {
    const licencesCount = licenceStore.licences.filter(l =>
      Boolean(this.state.selectedCustomers.find(o => o.id === l.customer_id)),
    ).length;
    if (licencesCount === 0) {
      this.setState({ showDeleteAlert: true });
    } else {
      this.setState({ showDeleteErrorAlert: true });
    }
  }

  async handleDeleteBulkCustomers() {
    this.setState({ showDeleteAlert: false });
    this.state.selectedCustomers.forEach(customer => {
      customerStore.delete(customer.id);
    });
  }

  getTotalMigrationsReplicas(customer: Customer) {
    const licences = licenceStore.licences.filter(
      l => l.customer_id === customer.id,
    );
    const total = licences.reduce(
      (t, l) => ({ r: t.r + l.replicas, m: t.m + l.migrations }),
      { r: 0, m: 0 },
    );
    return `${total.m} / ${total.r}`;
  }

  render() {
    const BulkActions: DropdownActionType[] = [
      {
        label: "Delete Customers",
        color: Style.palette.alert,
        action: () => {
          this.handleDeleteBulkAction();
        },
      },
    ];
    const customers = customerStore.customers;

    const columns = [
      { header: "No.", key: "index" },
      { header: "Customer", key: "name" },
      { header: "Email", key: "email" },
      { header: "Licences", key: "licences" },
      {
        header: "Total Migrations / Replicas",
        key: "total_migrations_replicas",
      },
      { header: "Created at", key: "created_at" },
      { header: "Created by", key: "created_by" },
    ];

    const dataWithIndex = customers.map((customer, index) => ({
      ...customer,
      index: index + 1,
      created_at: moment(customer.created_at).format("DD MMM YYYY HH:mm"),
      licences: licenceStore.licences.filter(l => l.customer_id === customer.id)
        .length,
      total_migrations_replicas: this.getTotalMigrationsReplicas(customer),
    }));

    return (
      <Wrapper>
        <ListTemplate
          title="Coriolis Customers"
          listComponent={
            <FilterListWrapper
              filterItems={[{ label: "All", value: "all" }]}
              dropdownActions={BulkActions}
              onSelectedItemsChange={selectedCustomers => {
                this.setState({ selectedCustomers });
              }}
              selectionLabel="customers"
              loading={customerStore.loading}
              items={customers}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              itemFilterFunction={(...args) => this.itemFilterFunction(...args)}
              renderItemComponent={options => {
                const customer: Customer = options.item;
                return (
                  <FilterListItem
                    {...options}
                    image={customerImage}
                    title={customer.name}
                    subtitle={customer.description}
                    columns={this.getListColumns(options.item)}
                  />
                );
              }}
              emptyListImage={customerImage}
              emptyListMessage="You don't have any created customers"
              emptyListExtraMessage="You need to create a new customer in order to create licences"
              emptyListButtonLabel="New Customer"
              onEmptyListButtonClick={() => {
                this.setState({ showCustomerModal: true });
              }}
            />
          }
          footerText={customers.length ? `${customers.length} customers` : ""}
        />
        <ExportButtonWrapper>
          <ExportPdfButton
            data={dataWithIndex}
            title="Customers"
            columns={columns}
          />
        </ExportButtonWrapper>
        {this.state.showCustomerModal ? (
          <CustomerModal
            onRequestClose={() => {
              this.setState({ showCustomerModal: false });
            }}
          />
        ) : null}
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Customers?"
            message="Are you sure you want to delete the selected customers?"
            extraMessage="Deleting a Coriolis Customer is permanent!"
            onConfirmation={() => {
              this.handleDeleteBulkCustomers();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Customers"
            message="Some of the selected customers have licences associated with them"
            extraMessage="You must first delete the associated licences"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function CustomersPageWithNavigate() {
  const navigate = useNavigate();
  return <CustomersPage onNavigate={navigate} />;
}

export default CustomersPageWithNavigate;
